/**
 * Pipelines · Informes — Documentos marcados como "Informe de visita".
 *
 * Lista los adjuntos de pipeline clasificados como "Informe de visita", con el
 * nombre estandarizado "Informe visitas [cliente] [pipeline] [usuario]".
 * Usuario normal: ver + descargar. Admin: además puede eliminar.
 */
import { useState, useEffect, useCallback } from "react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  ClipboardCheck, Search as SearchIcon, Download, Trash2, Loader2, FileText, User, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InformesDocumentosPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("search", search);
      const r = await fetch(`${API_URL}/api/pipelines/informes-visitas?${qs}`, getAuthHeaders());
      const d = await r.json();
      setItems(d.items || []);
    } catch { toast.error("Error al cargar los informes"); }
    setLoading(false);
  }, [search, getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 300);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const download = async (it) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/informes-visitas/${it.id}/download`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ext = (it.name || "").includes(".") ? it.name.slice(it.name.lastIndexOf(".")) : "";
      link.href = url;
      link.download = (it.informe_nombre || "Informe visitas") + ext;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const del = async (it) => {
    if (!window.confirm(`¿Eliminar el informe "${it.informe_nombre}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/informes-visitas/${it.id}`, {
        method: "DELETE", headers: getAuthHeaders().headers,
      });
      if (!r.ok) { const ed = await r.json(); throw new Error(ed.detail || "Error"); }
      toast.success("Informe eliminado");
      fetchItems();
    } catch (err) { toast.error(err.message || "Error al eliminar"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-64">
        <div className="max-w-5xl mx-auto px-6 py-6" data-testid="informes-documentos-page">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-[#80BC00]" />
              <h1 className="text-xl font-bold text-slate-800">Informes</h1>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Informes de visita cargados desde los pipelines, identificados por cliente y pipeline.
          </p>

          <div className="relative mb-4 max-w-md">
            <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, pipeline o nombre..."
              className="pl-9"
              data-testid="informes-search-input"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-400" data-testid="informes-empty">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No hay informes de visita cargados.</p>
              <p className="text-xs mt-1">Al subir un adjunto en un pipeline y marcarlo como "Informe de visita", aparecerá aquí.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="informes-list">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                  data-testid={`informe-row-${it.id}`}
                >
                  <div className="w-9 h-9 rounded bg-[#80BC00]/10 flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-[#80BC00]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate" title={it.informe_nombre}>
                      {it.informe_nombre}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                      <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {it.cliente || "—"}</span>
                      <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {it.pipeline_nombre || "—"}</span>
                      <span>{it.uploaded_by_name}</span>
                      <span>{it.uploaded_at ? new Date(it.uploaded_at).toLocaleDateString("es-DO") : ""}</span>
                      {it.size != null && <span>{(it.size / 1024).toFixed(1)} KB</span>}
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] flex-shrink-0 hidden sm:inline-flex">Informe de visita</Badge>
                  <Button
                    variant="outline" size="sm" onClick={() => download(it)}
                    data-testid={`informe-download-${it.id}`}
                  >
                    <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Descargar</span>
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="ghost" size="icon" className="text-rose-500 hover:bg-rose-50"
                      onClick={() => del(it)} data-testid={`informe-delete-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
